/**
 * Dark8-s Phase 19 — Operator Identity Panel
 *
 * Displays the current operator's identity, roles, and active status.
 * Shows disabled-action states and "insufficient role" messages.
 *
 * Governance invariants:
 *   - This panel is advisory only. Backend enforces all role gates.
 *   - No publish controls exist.
 *   - No upload controls exist.
 *   - publish_allowed is always false.
 */

import { useState, useEffect, useCallback } from "react";
import { Shield, User, AlertTriangle, RefreshCw } from "lucide-react";
import { getOperatorMe } from "../lib/api";

// ─── Role badge styles ────────────────────────────────────────────────────────

const ROLE_STYLES = {
    admin:          "text-[#ff2a2a] border-[#ff2a2a]/50 bg-[#ff2a2a]/10",
    reviewer:       "text-[#22c55e] border-[#22c55e]/30 bg-[#22c55e]/5",
    asset_reviewer: "text-[#ffb800] border-[#ffb800]/30 bg-[#ffb800]/5",
    exporter:       "text-[#60a5fa] border-[#60a5fa]/30 bg-[#60a5fa]/5",
    viewer:         "text-[#8a8a8a] border-[#262626] bg-transparent",
};

const ROLE_LABELS = {
    admin:          "Admin",
    reviewer:       "Reviewer",
    asset_reviewer: "Asset Reviewer",
    exporter:       "Exporter",
    viewer:         "Viewer",
};

// ─── RoleBadge ────────────────────────────────────────────────────────────────

export const RoleBadge = ({ role }) => {
    const cls = ROLE_STYLES[role] || "text-[#8a8a8a] border-[#262626] bg-transparent";
    return (
        <span
            data-testid={`role-badge-${role}`}
            className={`inline-block border px-2 py-0.5 font-mono-d8 text-[10px] uppercase tracking-widest ${cls}`}
        >
            {ROLE_LABELS[role] || role}
        </span>
    );
};

// ─── InsufficientRoleMessage ──────────────────────────────────────────────────

export const InsufficientRoleMessage = ({ action, requiredRoles }) => (
    <div
        data-testid="insufficient-role-message"
        className="flex items-center gap-2 border border-[#ff2a2a]/30 bg-[#ff2a2a]/5 px-3 py-2 font-mono-d8 text-[10px] text-[#ff2a2a]"
    >
        <AlertTriangle className="w-3 h-3 shrink-0" />
        <span>
            Insufficient role for <strong>{action}</strong>.
            Required: {requiredRoles?.join(", ") || "higher role"}.
            Backend enforces this gate.
        </span>
    </div>
);

// ─── DisabledActionButton ─────────────────────────────────────────────────────

export const DisabledActionButton = ({ label, reason }) => (
    <div data-testid="disabled-action-button" className="space-y-1">
        <button
            disabled
            className="inline-flex items-center gap-2 bg-[#1a1a1a] border border-[#262626] text-[#4a4a4a] font-heading font-bold uppercase tracking-widest px-6 py-3 cursor-not-allowed opacity-50"
        >
            <Shield className="w-4 h-4" />
            {label}
        </button>
        {reason && (
            <div className="font-mono-d8 text-[10px] text-[#8a8a8a]">{reason}</div>
        )}
    </div>
);

// ─── OperatorIdentityPanel ────────────────────────────────────────────────────

export const OperatorIdentityPanel = () => {
    const [identity, setIdentity] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getOperatorMe();
            setIdentity(data);
        } catch {
            setIdentity(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <div
                data-testid="operator-identity-panel"
                className="border border-[#262626] bg-[#0a0a0a] px-4 py-3 font-mono-d8 text-[10px] text-[#8a8a8a]"
            >
                <RefreshCw className="w-3 h-3 animate-spin inline mr-2" />
                Loading operator identity…
            </div>
        );
    }

    if (!identity) {
        return (
            <div
                data-testid="operator-identity-panel"
                className="border border-[#262626] bg-[#0a0a0a] px-4 py-3 font-mono-d8 text-[10px] text-[#8a8a8a]"
            >
                <span className="text-[#ff2a2a]">No active session.</span>{" "}
                Internal mode required. Role gates are enforced by the backend.
            </div>
        );
    }

    return (
        <div
            data-testid="operator-identity-panel"
            className="border border-[#262626] bg-[#0a0a0a] px-4 py-3 space-y-2"
        >
            <div className="flex items-center gap-2">
                <User className="w-3 h-3 text-[#8a8a8a]" />
                <span className="font-mono-d8 text-[10px] text-[#8a8a8a] uppercase tracking-widest">
                    Operator
                </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <span
                    data-testid="operator-display-name"
                    className="font-mono-d8 text-xs text-[#f5f5f0]"
                >
                    {identity.display_name || identity.actor_id}
                </span>
                {!identity.active && (
                    <span className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest border border-[#ff2a2a]/30 px-2 py-0.5">
                        Inactive
                    </span>
                )}
            </div>
            <div className="flex flex-wrap gap-1">
                {(identity.roles || []).map((role) => (
                    <RoleBadge key={role} role={role} />
                ))}
                {(!identity.roles || identity.roles.length === 0) && (
                    <span className="font-mono-d8 text-[10px] text-[#8a8a8a]">No roles assigned</span>
                )}
            </div>
            <div className="font-mono-d8 text-[10px] text-[#4a4a4a] flex items-center gap-1">
                <Shield className="w-3 h-3" />
                publish_allowed=false — Backend enforces all role gates.
            </div>
        </div>
    );
};

export default OperatorIdentityPanel;
